/**
 * Fluxo de uma operação enviada para aprovação, reaberto no montador como na
 * tela de criação — mas só para leitura: o que foi enviado não muda aqui, salvo
 * o roteiro fiscal, onde o time Tax ajusta a ordem de emissão das NFs e corrige
 * valores (CFOP, ICMS, PIS/COFINS, IPI e CSTs) — o calculado fica riscado. No
 * roteiro contábil, o time Contábil corrige do mesmo modo as colunas de evento,
 * lançamentos, código IVA e parametrização (NF e operação não mudam).
 * Quem aprova (Contábil, Tax, Negócio) classifica a operação pelo seu grupo.
 */

import { ArrowLeft, Check, PencilLine, X } from "lucide-react";
import * as React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ETAPAS, SeloAprovacao } from "@/components/aprovacoes/SeloAprovacao";
import type { OrigemEnvio } from "@/components/aprovacoes/TabelaEnvios";
import { MontadorOperacao } from "@/components/canvas/MontadorOperacao";
import {
  type Corrigir,
  type EstadoGravacao,
  listarCorrecoes,
} from "@/components/fiscal/CorrecaoFiscal";
import {
  type CorrigirContabil,
  listarCorrecoesContabil,
} from "@/components/fiscal/RoteiroContabil";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useClassificar,
  useEnvio,
  useCorrigirContabilEnvio,
  useCorrigirValoresEnvio,
  useReordenarNfsEnvio,
} from "@/hooks/queries";
import type { EstadoOperacao } from "@/hooks/useOperacao";
import { useAuth } from "@/lib/auth";
import { dataBR } from "@/lib/format";
import type {
  CorrecaoTax,
  EnvioAprovacaoDetalhe,
  SubstituicoesContabil,
  SubstituicoesNfs,
  TipoAprovacao,
} from "@/types";

/** O time Tax vê só o desenho da operação e o lado fiscal. */
const CARDS_TAX = ["fluxo", "resumo", "roteiro-fiscal"];

const ORIGEM_PADRAO: OrigemEnvio = { para: "/minhas-operacoes", rotulo: "Minhas operações" };

function estadoSalvo({ nome, pedido }: EnvioAprovacaoDetalhe): EstadoOperacao {
  return {
    nome: pedido.nome ?? nome,
    nodes: pedido.nodes,
    edges: pedido.edges,
    produto: pedido.produto,
    anidroDiferido: pedido.anidroDiferido ?? true,
    precoCompra: String(pedido.precoCompra ?? ""),
    precoVenda: String(pedido.precoVenda ?? ""),
    volume: String(pedido.volume ?? ""),
    ordemNfs: pedido.ordemNfs ?? [],
    baloes: pedido.baloes ?? {},
  };
}

/**
 * Correções gravadas no envio. As primeiras versões guardavam só o texto do
 * valor — viram correções sem motivo nem autor.
 */
function correcoesDoEnvio(envio: EnvioAprovacaoDetalhe | undefined): SubstituicoesNfs {
  const bruto = (envio?.pedido.substituicoesNfs ?? {}) as Record<
    string,
    Record<string, CorrecaoTax | string>
  >;
  return Object.fromEntries(
    Object.entries(bruto).map(([chave, campos]) => [
      chave,
      Object.fromEntries(
        Object.entries(campos).map(([campo, c]) => [
          campo,
          typeof c === "string" ? { valor: c, motivo: "" } : c,
        ]),
      ),
    ]),
  );
}

/**
 * Correções do Tax: cada "Aplicar" ou "Restaurar" grava o mapa inteiro na hora
 * — a tela já mostra o resultado e volta atrás se o servidor recusar.
 */
function useCorrecoes(envio: EnvioAprovacaoDetalhe | undefined) {
  const gravar = useCorrigirValoresEnvio(envio?.id ?? "");
  const correcoes = correcoesDoEnvio(envio);

  const enviar = (subs: SubstituicoesNfs) =>
    gravar.mutate(subs, { onError: (erro: Error) => toast.error(erro.message) });

  const corrigir: Corrigir = (chave, campo, correcao) => {
    const { [campo]: _, ...outros } = correcoes[chave] ?? {};
    const campos = correcao ? { ...outros, [campo]: correcao } : outros;
    const { [chave]: __, ...resto } = correcoes;
    enviar(Object.keys(campos).length ? { ...resto, [chave]: campos } : resto);
  };

  const gravacao: EstadoGravacao = gravar.isPending
    ? "salvando"
    : gravar.isError
      ? "erro"
      : gravar.isSuccess
        ? "salvo"
        : null;
  const tentarDeNovo = () => gravar.variables && enviar(gravar.variables);

  return { correcoes, corrigir, gravacao, tentarDeNovo };
}

/** Correções do Contábil: a mesma gravação imediata das do Tax, no roteiro contábil. */
function useCorrecoesContabil(envio: EnvioAprovacaoDetalhe | undefined) {
  const gravar = useCorrigirContabilEnvio(envio?.id ?? "");
  const correcoes = (envio?.pedido.substituicoesContabil ?? {}) as SubstituicoesContabil;

  const enviar = (subs: SubstituicoesContabil) =>
    gravar.mutate(subs, { onError: (erro: Error) => toast.error(erro.message) });

  const corrigir: CorrigirContabil = (chave, mudancas) => {
    const campos: NonNullable<SubstituicoesContabil[string]> = { ...correcoes[chave] };
    for (const [campo, correcao] of Object.entries(mudancas) as [
      keyof typeof campos,
      { valor: string; motivo: string } | null,
    ][]) {
      if (correcao) campos[campo] = correcao;
      else delete campos[campo];
    }
    const { [chave]: _, ...resto } = correcoes;
    enviar(Object.keys(campos).length ? { ...resto, [chave]: campos } : resto);
  };

  const gravacao: EstadoGravacao = gravar.isPending
    ? "salvando"
    : gravar.isError
      ? "erro"
      : gravar.isSuccess
        ? "salvo"
        : null;
  const tentarDeNovo = () => gravar.variables && enviar(gravar.variables);

  return { correcoes, corrigir, gravacao, tentarDeNovo };
}

function irParaRoteiro(id = "roteiro-fiscal") {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function CardAprovacao({
  envio,
  tipo,
  correcoes,
  correcoesContabil,
}: {
  envio: EnvioAprovacaoDetalhe;
  /** Etapa que o usuário classifica; `null` só mostra o estado. */
  tipo: TipoAprovacao | null;
  correcoes: SubstituicoesNfs;
  correcoesContabil: SubstituicoesContabil;
}) {
  const classificar = useClassificar(envio.id);
  const [reprovando, setReprovando] = React.useState(false);
  const [confirmando, setConfirmando] = React.useState(false);
  const lista = listarCorrecoes(correcoes);
  const comCorrecoes = tipo === "tax" && lista.length > 0;
  const listaContabil = listarCorrecoesContabil(correcoesContabil);
  const comCorrecoesContabil = tipo === "contabil" && listaContabil.length > 0;
  const confirmaCorrecoes = comCorrecoes || comCorrecoesContabil;
  const listaConfirmacao = comCorrecoes ? lista : listaContabil;
  const [comentario, setComentario] = React.useState("");
  const minha = tipo ? envio.aprovacoes[tipo] : null;

  const decidir = (decisao: "aprovado" | "reprovado", texto = "") =>
    classificar.mutate(
      { decisao, comentario: texto },
      {
        onSuccess: () => {
          toast.success(
            decisao === "aprovado" ? "Operação aprovada." : "Operação reprovada.",
          );
          setReprovando(false);
          setConfirmando(false);
          setComentario("");
        },
        onError: (erro: Error) => toast.error(erro.message),
      },
    );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Aprovação</CardTitle>
            <CardDescription>
              Enviada por {envio.autor ?? "usuário removido"} em {dataBR(envio.criadoEm)}.
            </CardDescription>
            {lista.length > 0 && (
              <button
                type="button"
                onClick={() => irParaRoteiro()}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800 transition-colors hover:bg-amber-200"
              >
                <PencilLine className="size-3.5" />
                {lista.length} {lista.length === 1 ? "valor corrigido" : "valores corrigidos"} pelo
                Tax no roteiro fiscal
              </button>
            )}
            {listaContabil.length > 0 && (
              <button
                type="button"
                onClick={() => irParaRoteiro("roteiro-contabil")}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800 transition-colors hover:bg-amber-200"
              >
                <PencilLine className="size-3.5" />
                {listaContabil.length}{" "}
                {listaContabil.length === 1 ? "valor corrigido" : "valores corrigidos"} pelo
                Contábil no roteiro contábil
              </button>
            )}
          </div>
          {tipo && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={classificar.isPending || minha?.decisao === "aprovado"}
                // Abre a confirmação: observação opcional e, no Tax, as correções que travam.
                onClick={() => {
                  setComentario("");
                  setConfirmando(true);
                }}
              >
                <Check />
                Aprovar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive"
                disabled={classificar.isPending}
                onClick={() => {
                  setComentario(minha?.decisao === "reprovado" ? minha.comentario : "");
                  setReprovando(true);
                }}
              >
                <X />
                Reprovar
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <dl className="grid gap-3 sm:grid-cols-3">
          {ETAPAS.map((etapa) => {
            const c = envio.aprovacoes[etapa.tipo];
            return (
              <div key={etapa.tipo} className="rounded-lg border border-border p-3">
                <dt className="mb-1.5 flex items-center justify-between gap-2 text-sm font-bold">
                  {etapa.rotulo}
                  <SeloAprovacao classificacao={c} />
                </dt>
                <dd className="text-xs text-muted-foreground">
                  {c ? (
                    <>
                      {c.por ?? "Usuário removido"} · {dataBR(c.em)}
                      {c.comentario && (
                        <p className="mt-1 whitespace-pre-line text-foreground">
                          “{c.comentario}”
                        </p>
                      )}
                    </>
                  ) : (
                    "Aguardando classificação."
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </CardContent>

      <Dialog open={confirmando} onOpenChange={setConfirmando}>
        <DialogContent className="w-[min(40rem,94vw)]">
          <DialogHeader>
            <DialogTitle>
              {confirmaCorrecoes
                ? `Aprovar com ${listaConfirmacao.length === 1 ? "a correção" : "as correções"}?`
                : `Aprovar “${envio.nome}”`}
            </DialogTitle>
            <DialogDescription>
              {comCorrecoes
                ? "O PDF e o roteiro fiscal passam a valer com os valores abaixo. Depois de aprovar, a ordem das NFs e as correções não podem mais mudar."
                : comCorrecoesContabil
                  ? "O PDF e o roteiro contábil passam a valer com os textos abaixo. Depois de aprovar, as correções não podem mais mudar."
                  : "A observação é opcional e fica visível para o autor e no Repositório TAX."}
            </DialogDescription>
          </DialogHeader>
          {confirmaCorrecoes && (
          <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-lg border border-border text-sm">
            {listaConfirmacao.map(({ chave, campo, rotulo, correcao }) => (
              <li key={`${chave}-${campo}`} className="space-y-0.5 px-3 py-2">
                <p className="text-xs text-muted-foreground">
                  {correcao.nf ?? chave} · <strong className="text-foreground">{rotulo}</strong>
                </p>
                <p className="numerico whitespace-pre-line">
                  <span className="text-muted-foreground line-through">
                    {correcao.original ?? "—"}
                  </span>{" "}
                  → <strong className="text-amber-700">{correcao.valor}</strong>
                </p>
                {correcao.motivo && (
                  <p className="text-xs italic text-muted-foreground">“{correcao.motivo}”</p>
                )}
              </li>
            ))}
          </ul>
          )}
          <div className="space-y-2">
            <Label htmlFor="observacao-aprovacao">Observação (opcional)</Label>
            <Textarea
              id="observacao-aprovacao"
              rows={3}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              placeholder="Ex.: aprovado, atentar para a ST na entrada."
            />
          </div>
          <div className="flex justify-end gap-2">
            {confirmaCorrecoes ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirmando(false);
                  irParaRoteiro(comCorrecoes ? "roteiro-fiscal" : "roteiro-contabil");
                }}
              >
                Revisar no roteiro
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmando(false)}>
                Cancelar
              </Button>
            )}
            <Button
              disabled={classificar.isPending}
              onClick={() => decidir("aprovado", comentario.trim())}
            >
              <Check />
              Aprovar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reprovando} onOpenChange={setReprovando}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprovar “{envio.nome}”</DialogTitle>
            <DialogDescription>
              O motivo fica visível para o autor e no Repositório TAX.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(evento) => {
              evento.preventDefault();
              decidir("reprovado", comentario.trim());
            }}
          >
            <Label htmlFor="motivo-reprovacao">Motivo</Label>
            <Textarea
              id="motivo-reprovacao"
              autoFocus
              rows={4}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              placeholder="Ex.: o retorno simbólico deveria sair do armazém de saída física."
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setReprovando(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={!comentario.trim() || classificar.isPending}
              >
                Reprovar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function VisualizarOperacaoPage() {
  const { id } = useParams();
  const origem = (useLocation().state as OrigemEnvio | null) ?? ORIGEM_PADRAO;
  const { usuario, tipoAprovacao } = useAuth();
  const { data: envio, isLoading, error } = useEnvio(id);
  const reordenar = useReordenarNfsEnvio(id ?? "");
  const ehTax = usuario?.papel === "tax" || usuario?.papel === "admin";
  // Aprovada pelo Tax, o roteiro fiscal trava — ordem e correções (o backend também recusa).
  const ajusta = ehTax && envio?.aprovacoes.tax?.decisao !== "aprovado";
  const { correcoes, corrigir, gravacao, tentarDeNovo } = useCorrecoes(envio);
  // Só o Contábil corrige o roteiro contábil, e só até aprovar a própria etapa.
  const ajustaContabil =
    usuario?.papel === "contabil" && envio?.aprovacoes.contabil?.decisao !== "aprovado";
  const contabil = useCorrecoesContabil(envio);

  return (
    <div className="space-y-5">
      <Button asChild variant="ghost" size="sm">
        <Link to={origem.para}>
          <ArrowLeft />
          {origem.rotulo}
        </Link>
      </Button>

      {error ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm font-semibold text-destructive">
            {error.message}
          </CardContent>
        </Card>
      ) : isLoading || !envio ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          <CardAprovacao
            envio={envio}
            tipo={tipoAprovacao}
            correcoes={correcoes}
            correcoesContabil={contabil.correcoes}
          />
          <MontadorOperacao
            key={envio.id}
            contexto={envio.pedido.contexto}
            chaveModelo={`envio-${envio.id}`}
            titulo={envio.categoria}
            descricao={`Enviada para aprovação em ${dataBR(envio.criadoEm)} — ${
              ajusta
                ? "no roteiro fiscal, ajuste a ordem das NFs ou clique num valor para corrigi-lo."
                : ajustaContabil
                  ? "no roteiro contábil, clique numa coluna para corrigi-la."
                  : "somente visualização."
            }`}
            papeis={[]}
            produtos={[envio.pedido.produto]}
            salvo={estadoSalvo(envio)}
            somenteLeitura
            cardsVisiveis={ehTax ? CARDS_TAX : undefined}
            onReordenarNfs={
              ajusta
                ? (ordem) =>
                    reordenar.mutate(ordem, {
                      onError: (erro: Error) => toast.error(erro.message),
                    })
                : undefined
            }
            substituicoesNfs={correcoes}
            onCorrigirValor={ajusta ? corrigir : undefined}
            gravacaoCorrecoes={ajusta ? gravacao : null}
            onTentarGravarDeNovo={tentarDeNovo}
            substituicoesContabil={contabil.correcoes}
            onCorrigirContabil={ajustaContabil ? contabil.corrigir : undefined}
            gravacaoContabil={ajustaContabil ? contabil.gravacao : null}
            onTentarGravarContabilDeNovo={contabil.tentarDeNovo}
          />
        </>
      )}
    </div>
  );
}
